<template>
  <div class="education">
    <ResumeForm
      :formLine="formLine"
      :data="education"
      ref="form"
    >
      <template #dateRange="{ data, line }">
        <van-row class="row" :gutter="4">
          <van-col :span="11">
            <VanCustomField
              :field="line.startDate"
              :data="data"
            >
            </VanCustomField>
          </van-col>
          <van-col :span="2">
            <div class="line">{{ $t(`${i18nPrefix}.label.to`) }}</div>
          </van-col>
          <van-col :span="11">
            <VanCustomField
              :field="line.endDate"
              :data="data"
            >
            </VanCustomField>
          </van-col>
        </van-row>
      </template>
    </ResumeForm>
  </div>
</template>

<script>
import _ from 'lodash'
import ResumeForm from '@/components/mobile/resumeForm.vue'
import VanCustomField from '@/components/mobile/vanCustomField.vue'
import moment from 'moment'

export default {
  components: {
    ResumeForm,
    VanCustomField
  },
  props: {
    education: {
      type: Object,
      required: true
    },
    setting: {
      type: Object,
      default: null
    },
    educationLevelOptions: {
      type: Array,
      default: null
    },
    majorOptions: {
      type: Array,
      default: null
    },
    schoolOptions: {
      type: Array,
      default: null
    },
    academicRatingOptions: {
      type: Array,
      default: null
    },
    index: {
      default: null
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      isLowerLevel: false
    }
  },
  computed: {
    eduRequiredField () {
      return this.setting?.eduRequiredField || []
    },
    eduHiddenField () {
      return this.setting?.eduHiddenField || []
    },
    eduDisabledField () {
      return this.setting?.eduDisabledField || []
    },
    formLine () {
      const requiredMessage = this.$t('common.message.requiredFormField')
      const eduRequiredField = this.eduRequiredField?.length ? [...this.eduRequiredField] : ['degreeLevel', 'majorName', 'startDate', 'endDate', 'schoolName']
      const eduHiddenField = []
      if (!_.isEmpty(this.eduHiddenField)) {
        eduHiddenField.push(...this.eduHiddenField)
      }
      const eduDisabledField = []
      if (!_.isEmpty(this.eduDisabledField)) {
        eduDisabledField.push(...this.eduDisabledField)
      }
      const vm = this
      return [
        {
          elType: 'select',
          name: 'degreeLevel',
          hidden: eduHiddenField.includes('degreeLevel'),
          disabled: eduDisabledField.includes('degreeLevel'),
          options: {
            data: this.educationLevelOptions
          },
          handler: () => {
            this.checkEducationalLevel()
          },
          label: this.$t(`${this.i18nPrefix}.label.degreeLevel`),
          rules: [{ required: eduRequiredField.includes('degreeLevel'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'schoolId',
          selectValueName: 'schoolName',
          hidden: eduHiddenField.includes('school'),
          disabled: eduDisabledField.includes('school'),
          canNotInOptions: true,
          lazyLoad: true,
          options: { data: this.schoolOptions },
          handler: (field, selected, selectValue) => {
            const name = _.find(this.schoolOptions, ['value', selected])?.label || selectValue
            this.education.schoolName = name
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.inputAndSelect`),
          label: this.$t(`${this.i18nPrefix}.label.school`),
          rules: [{ required: eduRequiredField.includes('schoolName'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'select',
          name: 'majorId',
          selectValueName: 'majorName',
          hidden: this.isLowerLevel || eduHiddenField.includes('majorId'),
          disabled: eduDisabledField.includes('majorId'),
          canNotInOptions: true,
          lazyLoad: true,
          options: {
            data: this.majorOptions
          },
          handler: (field, selected, selectValue) => {
            const name = _.find(this.majorOptions, ['value', selected])?.label || selectValue
            this.education.majorName = name
          },
          placeholder: this.$t(`${this.i18nPrefix}.placeholder.inputAndSelect`),
          label: this.$t(`${this.i18nPrefix}.label.major`),
          rules: [{ required: eduRequiredField.includes('majorName'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          slotName: 'dateRange',
          hidden: eduHiddenField.includes('startDate') && eduHiddenField.includes('endDate'),
          startDate: {
            elType: 'date',
            hidden: eduHiddenField.includes('startDate'),
            name: 'startDate',
            formatter: (value) => {
              return value ? moment(value).format('YYYY-MM') : ''
            },
            before: {
              name: 'endDate'
            },
            placeholder: this.$t(`${this.i18nPrefix}.placeholder.startDate`),
            label: this.$t(`${this.i18nPrefix}.label.date`),
            dateTitle: this.$t(`${this.i18nPrefix}.placeholder.startDate`),
            rules: [{ required: eduRequiredField.includes('startDate'), message: requiredMessage, trigger: 'blur' }]
          },
          endDate: {
            elType: 'date',
            hidden: eduHiddenField.includes('endDate'),
            name: 'endDate',
            formatter: (value) => {
              return value ? moment(value).format('YYYY-MM') : ''
            },
            after: {
              name: 'startDate'
            },
            placeholder: this.$t(`${vm.i18nPrefix}.placeholder.endDate`),
            label: '',
            dateTitle: this.$t(`${this.i18nPrefix}.placeholder.endDate`),
            rules: [{ required: eduRequiredField.includes('endDate'), message: requiredMessage, trigger: 'blur' }]
          }
        },
        {
          elType: 'select',
          name: 'academicRating',
          hidden: this.isLowerLevel || eduHiddenField.includes('academicRating'),
          disabled: eduDisabledField.includes('academicRating'),
          notSearch: true,
          options: { data: this.academicRatingOptions },
          label: this.$t(`${this.i18nPrefix}.label.academicRating`),
          rules: [{ required: eduRequiredField.includes('academicRating'), message: requiredMessage, trigger: 'blur' }]
        },
        {
          elType: 'input',
          name: 'gpa',
          type: 'number',
          hidden: this.isLowerLevel || eduHiddenField.includes('gpa'),
          disabled: eduDisabledField.includes('gpa'),
          label: this.$t(`${this.i18nPrefix}.label.gpa`),
          rules: [
            { required: eduRequiredField.includes('gpa'), message: requiredMessage, trigger: 'blur' },
            { validator: this.validateGpa, message: this.$t(`${this.i18nPrefix}.message.gpaDataValueError`), trigger: 'onChange' }
          ]
        },
        {
          elType: 'text',
          name: 'summary',
          hidden: this.isLowerLevel || eduHiddenField.includes('summary'),
          disabled: eduDisabledField.includes('summary'),
          label: this.$t(`${this.i18nPrefix}.label.summary`),
          rules: [{ required: eduRequiredField.includes('summary'), message: requiredMessage, trigger: 'blur' }]
        }
      ]
    }
  },
  methods: {
    async submit () {
      const form = await this.getFrom()
      if (form) {
        const result = await form.submit()
        return result
      }
      return false
    },
    checkEducationalLevel () {
      const degreeLevel = this.education?.degreeLevel
      if (degreeLevel === 'Junior School' || degreeLevel === 'High School') {
        this.isLowerLevel = true
      } else {
        this.isLowerLevel = false
      }
    },
    validateGpa (value) {
      if (_.gt(value, 4)) {
        return false
      }
      return true
    },
    async getFrom () {
      let form = this.$refs.form
      if (!form) {
        this.$nextTick()
        form = this.$refs.form
      }
      return form
    }
  },
  watch: {
    education: {
      immediate: true,
      deep: true,
      handler () {
        this.checkEducationalLevel()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.education {

  .row {
    margin-bottom: 16px;

    .line {
      margin: 0 auto;
      position: relative;
      top: 24px;
      width: 10px;
      color: #909399;
      font-size: 14px;
    }
  }
}
</style>
